import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'reactstrap';
import { BiPlus } from 'react-icons/bi';
import useCustomers from '../hooks/useCustomers';
import DataTable from '../components/utility/dataTable';
import CreateCustomerModal from '../components/customer/createCustomerModal';
import { formatInstant, formatCountry } from '../utils/formatters';

// Column `key` is the JSON property name the API returns (used to render the
// cell via item[col.key]). The backend resolves sort/search against the
// model via case-insensitive reflection, so `key` must match the model
// property name too (or provide `path` for nav traversal e.g. "customer.name").
const ALL_COLUMNS = [
    { key: 'name',        label: 'Name',         system: true },
    { key: 'contactName', label: 'Contact',      system: false },
    { key: 'email',       label: 'Email',        system: false },
    { key: 'phone',       label: 'Phone',        system: false },
    { key: 'townCity',    label: 'Town / City',  system: false },
    { key: 'postcode',    label: 'Postcode',     system: false },
    { key: 'country',     label: 'Country',      system: false, render: formatCountry },
    { key: 'createdAt',   label: 'Created On',   system: false, render: formatInstant },
];

const DEFAULT_VISIBLE = {
    name: true,
    contactName: true,
    email: true,
    phone: true,
    townCity: true,
    postcode: false,
    country: false,
    createdAt: false,
};

const TABLE_NAME = 'marginpal-customers';

function loadVisibleColumns() {
    try {
        const stored = localStorage.getItem(TABLE_NAME);
        // Merge so newly-added columns pick up their default visibility
        // instead of silently being undefined (= hidden) for existing users.
        if (stored) return { ...DEFAULT_VISIBLE, ...JSON.parse(stored) };
    } catch { /* fall through to default */ }
    return DEFAULT_VISIBLE;
}

// Default set: every non-system column that's visible by default. Mirrors
// the derivation inside DataTable so "Reset to Defaults" and the initial
// filter seed agree on the same set.
const DEFAULT_SEARCH_FIELDS = ALL_COLUMNS
    .filter(col => !col.system && DEFAULT_VISIBLE[col.key])
    .map(col => col.path ?? col.key);

function loadSearchableFields() {
    try {
        const stored = localStorage.getItem(`${TABLE_NAME}_search`);
        if (stored) return JSON.parse(stored);
    } catch { /* fall through to default */ }
    return DEFAULT_SEARCH_FIELDS;
}

const DEFAULT_FILTER = {
    pageNumber: 1,
    pageSize: 20,
    sortOptions: { key: 'name', direction: 'ascending' },
    // Seeded in useState initializer below so the first query already has
    // the persisted searchFields — previously DataTable pushed them up on
    // mount, which fired a wasted initial request with empty fields.
    searchOptions: { searchTerm: '', searchFields: [] },
};

export default function CustomersPage() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState(() => ({
        ...DEFAULT_FILTER,
        searchOptions: { searchTerm: '', searchFields: loadSearchableFields() },
    }));
    const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
    const [addOpen, setAddOpen] = useState(false);

    const { data, isLoading, error } = useCustomers(filter);

    const columns = ALL_COLUMNS.filter(col => visibleColumns[col.key]);

    const handleFilterChange = ({ filterId, value }) => {
        setFilter(prev => {
            switch (filterId) {
                case 'pageNumber':
                    return { ...prev, pageNumber: Number(value) };
                case 'pageSize':
                    return { ...prev, pageSize: Number(value), pageNumber: 1 };
                case 'sortOptions':
                    return { ...prev, sortOptions: value, pageNumber: 1 };
                case 'search':
                    return {
                        ...prev,
                        pageNumber: 1,
                        searchOptions: { ...prev.searchOptions, searchTerm: value },
                    };
                case 'searchFields':
                    return {
                        ...prev,
                        pageNumber: 1,
                        searchOptions: { ...prev.searchOptions, searchFields: value },
                    };
                default:
                    return prev;
            }
        });
    };

    return (
        <div>
            <h1 className="fw-bold mb-1">Customers</h1>
            <p className="text-muted mb-4">Manage your customer records.</p>
            <DataTable
                tableName={TABLE_NAME}
                columns={columns}
                data={data?.items ?? []}
                isLoading={isLoading}
                error={error}
                totalRecords={data?.totalRecords ?? 0}
                filter={filter}
                filters={[]}
                onRowClick={(e, row) => navigate(`/customers/${row.id}`)}
                actions={[
                    <Button color="primary" size="sm" onClick={() => setAddOpen(true)}
                            className="d-inline-flex align-items-center gap-1">
                        <BiPlus /> Add Customer
                    </Button>
                ]}
                showSettings
                visibleColumns={visibleColumns}
                setVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE}
                settingsColumns={ALL_COLUMNS}
                onFilterChange={handleFilterChange}
                sortConfiguration={{ key: 'name', direction: 'ascending' }}
            />
            <CreateCustomerModal isOpen={addOpen} onClose={() => setAddOpen(false)} />
        </div>
    );
}
